import {
  Badge,
  FlexBox,
  FormField,
  FormGrid,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
} from '@dixa/lib-shared-habla';
import {
  HookFormSelectionListSingle,
  HookFormTextarea,
} from 'lib-shared-hook-form';
import { useForm } from 'react-hook-form';

import { createAppModal } from 'utils/modal';

import styles from './sidekickAskModal.module.scss';

type FieldValues = {
  answer: string;
};

/**
 * Asks the user for context before a prompt is sent to the Sidekick. A written
 * answer is optional, so confirming an empty field is a valid way through. When
 * there are choices, the user picks one of them instead.
 */
export const SidekickAskModal = createAppModal(
  { type: 'app:sidekickAsk' },
  ({ isOpen, modal, meta, hide }): JSX.Element => {
    const { title, label, placeholder, choices, defaultAnswer } = modal.arg;

    const form = useForm<FieldValues>({
      defaultValues: { answer: defaultAnswer ?? '' },
    });

    const handleCancel = () => {
      hide();
      meta?.resolve({ isCancelled: true });
    };

    const handleConfirm = ({ answer }: FieldValues) => {
      hide();
      meta?.resolve({ answer: answer.trim() });
    };

    return (
      <Modal isOpen={isOpen} onClose={handleCancel} className={styles.modal}>
        <ModalHead title={title} onClose={handleCancel} />

        <ModalBody>
          <FormGrid>
            <FormField>
              {choices ? (
                <HookFormSelectionListSingle
                  name="answer"
                  label={label}
                  control={form.control}
                  rules={{ required: true }}
                  componentProps={{
                    placeholder,
                    equalityFn: (a, b) => a === b,
                    getItemTitleByValue: (value) =>
                      choices.find((choice) => choice.value === value)?.label ??
                      '',
                  }}
                >
                  {choices.map((choice) => (
                    <SelectionList.Item key={choice.value} value={choice.value}>
                      <FlexBox gap="200" alignItems="center">
                        {choice.label}
                        {choice.badge && (
                          <Badge size="small" variant="success">
                            {choice.badge}
                          </Badge>
                        )}
                      </FlexBox>
                    </SelectionList.Item>
                  ))}
                </HookFormSelectionListSingle>
              ) : (
                <HookFormTextarea
                  name="answer"
                  label={label}
                  control={form.control}
                  componentProps={{
                    placeholder,
                    autoFocus: true,
                    height: 96,
                  }}
                />
              )}
            </FormField>
          </FormGrid>
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleCancel}
          confirmName="Ask Sidekick"
          confirmOnClick={form.handleSubmit(handleConfirm)}
        />
      </Modal>
    );
  },
);
